import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Camera, Trash2 } from "lucide-react";
import { invalidateUserDirectory } from "@/components/UserTag";

export function Avatar({ user, className = "h-8 w-8 text-xs" }) {
  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  if (user?.avatar) return <img src={user.avatar} alt={user.name} className={`${className} rounded-full object-cover`} data-testid="user-avatar-img" />;
  return <div className={`${className} rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 text-white flex items-center justify-center font-bold`}>{initials}</div>;
}

export const resizeImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const size = 256;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const s = Math.min(img.width, img.height);
      canvas.getContext("2d").drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, size, size);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function ProfileDialog({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", new_password: "", current_password: "" });
  const [avatar, setAvatar] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && user) {
      setForm({ name: user.name, email: user.email, new_password: "", current_password: "" });
      setAvatar(user.avatar || null);
    }
  }, [open, user]);

  const needCurrent = form.new_password || form.email.toLowerCase() !== (user?.email || "");

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pilih file gambar"); return; }
    try { setAvatar(await resizeImage(f)); } catch { toast.error("Gagal membaca gambar"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { name: form.name, email: form.email, avatar: avatar || "" };
      if (form.new_password) body.new_password = form.new_password;
      if (needCurrent) body.current_password = form.current_password;
      const { data } = await api.put("/auth/me", body);
      setUser(data);
      invalidateUserDirectory();
      toast.success("Profil diperbarui");
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle>Profil Saya</DialogTitle>
          <DialogDescription>Ubah nama, email, kata sandi, dan foto profil Anda.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-4">
          <Avatar user={{ ...user, name: form.name, avatar }} className="h-20 w-20 text-xl" />
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="profile-avatar-input" />
            <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="profile-avatar-upload"><Camera className="h-4 w-4" />Ganti Foto</Button>
            {avatar && <Button type="button" size="sm" variant="ghost" className="text-rose-600" onClick={() => setAvatar(null)} data-testid="profile-avatar-remove"><Trash2 className="h-4 w-4" />Hapus Foto</Button>}
          </div>
        </div>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="profile-email" /></div>
          <div className="space-y-1.5"><Label>Kata Sandi Baru (opsional)</Label><Input type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} placeholder="Kosongkan jika tidak diganti" data-testid="profile-new-password" /></div>
          {needCurrent && (
            <div className="space-y-1.5"><Label>Kata Sandi Saat Ini</Label><Input type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} placeholder="Wajib untuk ganti email / kata sandi" data-testid="profile-current-password" /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="profile-cancel">Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="profile-save">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
